import React, { useState } from 'react';
import { X, Upload, FileText, Loader2, CheckCircle, Tag } from 'lucide-react';
import evidenceAPI from '../../services/evidenceAPI';

interface UploadEvidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId?: number;
}

const UploadEvidenceModal: React.FC<UploadEvidenceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  projectId
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [evidenceName, setEvidenceName] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceType, setEvidenceType] = useState('manual_upload');
  const [complianceTags, setComplianceTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const evidenceTypes = [
    { value: 'manual_upload', label: 'General Evidence' },
    { value: 'penetration_test', label: 'Penetration Test Report' },
    { value: 'vendor_certification', label: 'Vendor Certification (SOC 2, ISO)' },
    { value: 'insurance_policy', label: 'Insurance Policy' },
    { value: 'legal_compliance', label: 'Legal Compliance Document' },
    { value: 'training_certificate', label: 'Training Certificate' },
    { value: 'audit_report', label: 'Audit Report' },
    { value: 'policy_document', label: 'Policy Document' }
  ];

  const commonComplianceTags = [
    'SOC2_CC1', 'SOC2_CC6', 'SOC2_CC7', 'SOC2_CC8',
    'ISO27001_A5', 'ISO27001_A9', 'ISO27001_A12', 'ISO27001_A13',
    'PCI_DSS_1', 'PCI_DSS_4', 'PCI_DSS_10', 'PCI_DSS_11',
    'OWASP_A01', 'OWASP_A04', 'OWASP_A07', 'OWASP_A08',
    'NIST_PR_AC', 'NIST_PR_DS', 'NIST_DE_CM',
    'GDPR_SECURITY', 'GDPR_PRIVACY'
  ];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    if (!evidenceName) {
      setEvidenceName(selectedFile.name);
    }
    setError('');
  };

  const handleAddTag = () => {
    if (newTag && !complianceTags.includes(newTag.toUpperCase())) {
      setComplianceTags([...complianceTags, newTag.toUpperCase()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setComplianceTags(complianceTags.filter(t => t !== tag));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('evidence_name', evidenceName);
      formData.append('description', description);
      formData.append('evidence_type', evidenceType);
      formData.append('compliance_tags', JSON.stringify(complianceTags));

      if (projectId) {
        formData.append('project_id', projectId.toString());
      }

      await evidenceAPI.uploadManualEvidence(formData);

      onSuccess();
      resetForm();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload evidence');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setEvidenceName('');
    setDescription('');
    setEvidenceType('manual_upload');
    setComplianceTags([]);
    setNewTag('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Manual Evidence
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence File
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop your file here, or{' '}
                    <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                      browse
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => e.target.files && handleFileChange(e.target.files[0])}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip,.json,.csv,.log,.txt"
                      />
                    </label>
                  </p>
                  <p className="text-xs text-gray-500">
                    Supported: PDF, DOC, XLS, PNG, JPG, ZIP, JSON, CSV, LOG, TXT (Max 100MB)
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Evidence Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence Name
            </label>
            <input
              type="text"
              value={evidenceName}
              onChange={(e) => setEvidenceName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Q4 2025 Penetration Test Report"
              required
            />
          </div>

          {/* Evidence Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evidence Type
            </label>
            <select
              value={evidenceType}
              onChange={(e) => setEvidenceType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {evidenceTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              placeholder="Describe what this evidence demonstrates..."
            />
          </div>

          {/* Compliance Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Compliance Tags
            </label>

            {/* Selected Tags */}
            {complianceTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {complianceTags.map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-blue-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add Tag Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Type or select a tag..."
                list="compliance-tags"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-sm"
              >
                Add
              </button>
            </div>

            <datalist id="compliance-tags">
              {commonComplianceTags.map(tag => (
                <option key={tag} value={tag} />
              ))}
            </datalist>

            <p className="text-xs text-gray-500 mt-2">
              Common tags: SOC2_CC*, ISO27001_A*, PCI_DSS_*, OWASP_A*, NIST_*, GDPR_*
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading || !file}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Evidence
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadEvidenceModal;
